import { useState, useEffect } from "react";

/* ═══════════════════════════════════════════════════════════════
   InvelpsAuth.jsx — Standalone Login + Signup
   Usage :
     <InvelpsAuth
       initialPage="login"           // "login" | "signup"
       onSuccess={(user) => ...}     // callback après connexion/inscription
       googleAuthUrl="/auth/google"
       linkedinAuthUrl="/auth/linkedin"
     />
   ═══════════════════════════════════════════════════════════════ */

/* ─── TOKENS ─────────────────────────────────────────────────── */
const C = { green:"#6f8a5b", amber:"#caa85e", orange:"#d28a52", red:"#c6523f", gold:"#b59a5b" };

const LIGHT = {
  appBg:"#f2ede3", paper:"rgba(255,255,255,.94)", paperAlt:"#f7f5f0",
  paperSolid:"#fff", ink:"#18221e", ink2:"#2e3d35", muted:"#7a8a82",
  line:"rgba(30,50,38,.09)", line2:"rgba(30,50,38,.05)", rowH:"rgba(181,154,91,.09)",
};
const DARK = {
  appBg:"#0b1220", paper:"rgba(15,23,42,.90)", paperAlt:"rgba(15,23,42,.60)",
  paperSolid:"#111827", ink:"#e2e8f0", ink2:"#cbd5e1", muted:"#94a3b8",
  line:"rgba(148,163,184,.18)", line2:"rgba(148,163,184,.09)", rowH:"rgba(181,154,91,.09)",
};

function syncTheme(th) {
  const r = document.documentElement;
  [["--app-bg",th.appBg],["--paper",th.paper],["--paper-alt",th.paperAlt],
   ["--paper-solid",th.paperSolid],["--ink",th.ink],["--ink2",th.ink2],
   ["--muted",th.muted],["--line",th.line],["--line2",th.line2],["--row-h",th.rowH],
  ].forEach(([k,v]) => r.style.setProperty(k,v));
  document.body.style.background = th.appBg;
  document.body.style.color = th.ink;
}

const GLOBAL_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Mono:wght@400;600;700&display=swap'); *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;} html{-webkit-text-size-adjust:100%;} body{font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;min-height:100vh;} input,button,a{font-family:inherit;} input{font-size:16px!important;} @media(max-width:400px){ .inv-grid-2{grid-template-columns:1fr!important;} } input:focus{outline:none;} @keyframes spin   {to{transform:rotate(360deg)}} @keyframes fadeUp {from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} @keyframes fadeIn {from{opacity:0}to{opacity:1}}`;

/* ─── MICRO-COMPOSANTS ────────────────────────────────────────── */
const iSt = {
  flex:1, background:"none", border:"none", outline:"none",
  fontSize:13, fontWeight:600, fontFamily:"inherit",
};

function SLabel({ th, children }) {
  return (
    <div style={{ fontSize:10, fontWeight:800, textTransform:"uppercase",
      letterSpacing:".1em", color:th.muted, marginBottom:6 }}>
      {children}
    </div>
  );
}

function SInput({ th, value, onChange, onBlur, onKeyDown, placeholder, type="text", error }) {
  return (
    <div style={{ display:"flex", alignItems:"center", height:44, borderRadius:11,
      border:`1px solid ${error ? C.red : th.line}`,
      background:th.paperAlt, padding:"0 14px", transition:"border-color .15s" }}>
      <input value={value} onChange={onChange} onBlur={onBlur} onKeyDown={onKeyDown}
        placeholder={placeholder} type={type} style={{ ...iSt, color:th.ink }}/>
    </div>
  );
}

function SInputIcon({ th, icon, children, error, borderColor, extra }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, height:44, borderRadius:11,
      border:`1px solid ${borderColor || (error ? C.red : th.line)}`,
      background:th.paperAlt, padding:"0 14px", transition:"border-color .15s" }}>
      <span style={{ fontSize:13, color:th.muted, flexShrink:0 }}>{icon}</span>
      {children}
      {extra}
    </div>
  );
}

function FieldError({ text }) {
  return text
    ? <div style={{ fontSize:10, color:C.red, marginTop:4, fontWeight:600 }}>{text}</div>
    : null;
}

function Feedback({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ borderRadius:10, padding:"10px 14px", marginBottom:18, fontSize:12,
      fontWeight:600, animation:"fadeIn .2s ease",
      background: msg.type==="ok" ? `${C.green}18` : `${C.red}14`,
      border: `1px solid ${msg.type==="ok" ? C.green : C.red}44`,
      color: msg.type==="ok" ? C.green : C.red }}>
      {msg.type==="ok" ? "✓ " : "✕ "}{msg.text}
    </div>
  );
}

function OAuthRow({ th, googleUrl, linkedinUrl }) {
  return (
    <>
      <div style={{ display:"flex", alignItems:"center", gap:10, margin:"22px 0 16px" }}>
        <div style={{ flex:1, height:1, background:th.line }}/>
        <span style={{ fontSize:9, color:th.muted, fontWeight:700, flexShrink:0, whiteSpace:"nowrap" }}>
          OU CONTINUER AVEC
        </span>
        <div style={{ flex:1, height:1, background:th.line }}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10 }}>
        {[
          {icon:"G",  label:"Google",  bg:"#4285F4", href:googleUrl},
          {icon:"in", label:"LinkedIn",bg:"#0A66C2", href:linkedinUrl},
        ].map(({icon,label,bg,href})=>(
          <a key={label} href={href}
            style={{ height:40, borderRadius:10, border:`1px solid ${th.line}`,
              background:th.paperAlt, fontSize:12, fontWeight:700, color:th.ink,
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              textDecoration:"none", cursor:"pointer", transition:"background .12s" }}
            onMouseEnter={e=>e.currentTarget.style.background=th.rowH}
            onMouseLeave={e=>e.currentTarget.style.background=th.paperAlt}>
            <span style={{ width:18, height:18, borderRadius:4, background:bg, flexShrink:0,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:9, fontWeight:900, color:"#fff" }}>{icon}</span>
            {label}
          </a>
        ))}
      </div>
    </>
  );
}

function CGUCheck({ th }) {
  const [checked, setChecked] = useState(false);
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:20,
      padding:"10px 12px", borderRadius:10, background:th.paperAlt, border:`1px solid ${th.line}` }}>
      <div onClick={()=>setChecked(c=>!c)}
        style={{ width:18, height:18, borderRadius:5, flexShrink:0, marginTop:1,
          border:`2px solid ${checked ? C.gold : th.muted}`, cursor:"pointer",
          background:checked ? C.gold : "transparent",
          display:"flex", alignItems:"center", justifyContent:"center", transition:"all .15s" }}>
        {checked && <span style={{ fontSize:10, color:"#fff", fontWeight:900 }}>✓</span>}
      </div>
      <div style={{ fontSize:11, color:th.muted, fontWeight:600, lineHeight:1.6 }}>
        J'accepte les{" "}
        <span style={{ color:C.gold, fontWeight:700, cursor:"pointer" }}>Conditions d'utilisation</span>
        {" "}et la{" "}
        <span style={{ color:C.gold, fontWeight:700, cursor:"pointer" }}>Politique de confidentialité</span>
        {" "}d'Invelps.
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.4)",
      borderTopColor:"#fff", borderRadius:999, display:"inline-block",
      animation:"spin .7s linear infinite" }}/>
  );
}

function CtaButton({ onClick, disabled, loading, children }) {
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{ width:"100%", height:46, borderRadius:12, border:"none", marginTop:6,
        background: (disabled||loading) ? "rgba(181,154,91,.4)" : `linear-gradient(135deg,${C.gold},#c9a84c)`,
        color:"#fff", fontSize:13, fontWeight:800,
        cursor: (disabled||loading) ? "not-allowed" : "pointer",
        letterSpacing:"-.01em",
        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        boxShadow: (disabled||loading) ? "none" : "0 4px 16px rgba(181,154,91,.35)",
        transition:"opacity .15s" }}>
      {loading ? <><Spinner/> Traitement…</> : children}
    </button>
  );
}

/* ─── HEADER CARD ─────────────────────────────────────────────── */
function AuthHeader({ title, sub, children }) {
  return (
    <div style={{ background:"linear-gradient(135deg,#2a4f3f 0%,#1e3a2e 100%)",
      padding:"clamp(24px,5vw,36px) clamp(20px,5vw,40px) 28px", position:"relative" }}>
      <div style={{ position:"absolute", inset:0, opacity:.04, pointerEvents:"none",
        backgroundImage:"radial-gradient(circle at 1px 1px,#fff 1px,transparent 0)",
        backgroundSize:"20px 20px" }}/>
      {children}
      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10, position:"relative" }}>
        <div style={{ width:2.5, height:22, background:C.gold, borderRadius:2 }}/>
        <span style={{ fontFamily:"'Cormorant Garamond',Georgia,serif",
          fontSize:18, fontWeight:700, color:C.gold, letterSpacing:".14em" }}>INVELPS</span>
      </div>
      <div style={{ fontFamily:"'Cormorant Garamond',Georgia,serif",
        fontSize:"clamp(20px,3vw,26px)", fontWeight:700, color:"#fff",
        letterSpacing:"-.01em", marginBottom:5, position:"relative" }}>
        {title}
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", fontWeight:600 }}>{sub}</div>
    </div>
  );
}

/* ─── LOGIN PAGE ──────────────────────────────────────────────── */
function LoginPage({ onGoSignup, onSuccess, googleUrl, linkedinUrl, th, loginSubmit, onForgotPassword }) {
  const [mode, setMode]       = useState("login");
  const [email, setEmail]     = useState("");
  const [pwd, setPwd]         = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState(null);

  const reset = m => { setMode(m); setMsg(null); setPwd(""); };

  const validate = () => {
    if (!email.trim()) return "Adresse e-mail requise.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Adresse e-mail invalide.";
    if (mode !== "forgot" && !pwd) return "Mot de passe requis.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setMsg({ type: "err", text: err }); return; }
    if (mode === "forgot") {
      if (onForgotPassword) {
        onForgotPassword(email.trim());
        return;
      }
      setMsg({ type: "ok", text: `Lien de réinitialisation envoyé à ${email}` });
      return;
    }
    if (loginSubmit) {
      setLoading(true);
      setMsg(null);
      try {
        await loginSubmit({ email: email.trim(), password: pwd });
        setMsg({ type: "ok", text: "Connexion réussie. Redirection…" });
        onSuccess && onSuccess({ email: email.trim() });
      } catch (e) {
        setMsg({ type: "err", text: e?.message || "Erreur de connexion." });
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setMsg(null);
    setTimeout(() => {
      setLoading(false);
      setMsg({ type: "ok", text: "Connexion réussie. Redirection…" });
      setTimeout(() => onSuccess && onSuccess({ email }), 1000);
    }, 900);
  };

  const LABEL = {
    login:  { title:"Connexion",           sub:"Accédez à votre espace d'analyse",  cta:"Se connecter" },
    forgot: { title:"Mot de passe oublié", sub:"Récupérez l'accès à votre compte", cta:"Envoyer le lien" },
  }[mode] || { title:"Connexion", sub:"", cta:"Se connecter" };

  return (
    <div style={{ width:"100%", maxWidth:440,
      borderRadius:24, border:`1px solid ${th.line}`, background:th.paper,
      boxShadow:"0 24px 64px rgba(16,36,24,.13)", overflow:"hidden",
      animation:"fadeUp .35s ease both" }}>

      <AuthHeader title={LABEL.title} sub={LABEL.sub}/>

      <div style={{ padding:"clamp(20px,5vw,32px) clamp(16px,5vw,40px) 28px" }}>
        <Feedback msg={msg}/>

        {/* Email */}
        <div style={{ marginBottom:14 }}>
          <SLabel th={th}>Adresse e-mail</SLabel>
          <SInputIcon th={th} icon="✉">
            <input value={email} onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              placeholder="email@exemple.com" type="email"
              style={{ ...iSt, color:th.ink }}/>
          </SInputIcon>
        </div>

        {/* Mot de passe */}
        {mode !== "forgot" && (
          <div style={{ marginBottom:14 }}>
            <SLabel th={th}>Mot de passe</SLabel>
            <SInputIcon th={th} icon="◈"
              extra={
                <button onClick={()=>setShowPwd(s=>!s)}
                  style={{ background:"none", border:"none", color:th.muted,
                    fontSize:11, cursor:"pointer", flexShrink:0, padding:0 }}>
                  {showPwd?"Cacher":"Voir"}
                </button>
              }>
              <input value={pwd} onChange={e=>setPwd(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&submit()}
                placeholder="Votre mot de passe"
                type={showPwd?"text":"password"}
                style={{ ...iSt, color:th.ink }}/>
            </SInputIcon>
          </div>
        )}

        <CtaButton onClick={submit} loading={loading}>{LABEL.cta}</CtaButton>

        {/* Liens */}
        <div style={{ marginTop:18, display:"flex", flexDirection:"column", gap:8, alignItems:"center" }}>
          {mode === "login" && (
            <>
              <div style={{ fontSize:11, color:th.muted, fontWeight:600 }}>
                Pas encore de compte ?{" "}
                <button onClick={onGoSignup}
                  style={{ background:"none", border:"none", color:C.gold,
                    fontWeight:800, fontSize:11, cursor:"pointer", padding:0 }}>
                  Créer un compte
                </button>
              </div>
              <button onClick={()=>reset("forgot")}
                style={{ background:"none", border:"none", color:th.muted,
                  fontWeight:700, fontSize:11, cursor:"pointer", textDecoration:"underline" }}>
                Mot de passe oublié ?
              </button>
            </>
          )}
          {mode === "forgot" && (
            <button onClick={()=>reset("login")}
              style={{ background:"none", border:"none", color:th.muted,
                fontWeight:700, fontSize:11, cursor:"pointer", textDecoration:"underline" }}>
              ← Retour à la connexion
            </button>
          )}
        </div>

        <OAuthRow th={th} googleUrl={googleUrl} linkedinUrl={linkedinUrl}/>
      </div>

      <div style={{ padding:"14px clamp(16px,5vw,40px) 20px", borderTop:`1px solid ${th.line2}`, textAlign:"center" }}>
        <div style={{ fontSize:10, color:th.muted, fontWeight:600, lineHeight:1.6 }}>
          En continuant, vous acceptez nos{" "}
          <span style={{ color:C.gold, fontWeight:700, cursor:"pointer" }}>Conditions d'utilisation</span>
          {" "}et notre{" "}
          <span style={{ color:C.gold, fontWeight:700, cursor:"pointer" }}>Politique de confidentialité</span>.
        </div>
      </div>
    </div>
  );
}

/* ─── SIGNUP PAGE ─────────────────────────────────────────────── */
function SignupPage({ onGoLogin, onSuccess, googleUrl, linkedinUrl, th, signupSubmit }) {
  const [step,      setStep]      = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [pwd,       setPwd]       = useState("");
  const [pwd2,      setPwd2]      = useState("");
  const [showPwd,   setShowPwd]   = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [touched,   setTouched]   = useState({});

  const touch = f => setTouched(p => ({...p, [f]:true}));

  const errors = {
    firstName: !firstName.trim() ? "Prénom requis" : null,
    lastName:  !lastName.trim()  ? "Nom requis"   : null,
    email:     !email.trim() ? "E-mail requis"
               : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? "E-mail invalide" : null,
    pwd:  !pwd ? "Mot de passe requis" : pwd.length < 8 ? "8 caractères minimum" : null,
    pwd2: !pwd2 ? "Confirmation requise" : pwd2 !== pwd ? "Ne correspond pas" : null,
  };

  const step1Ok = !errors.firstName && !errors.lastName && !errors.email;
  const step2Ok = !errors.pwd && !errors.pwd2;

  const nextStep = () => {
    setTouched({firstName:true, lastName:true, email:true});
    if (step1Ok) setStep(2);
  };

  const submit = async () => {
    setTouched({ firstName: true, lastName: true, email: true, pwd: true, pwd2: true });
    if (!step1Ok || !step2Ok) return;
    if (signupSubmit) {
      setLoading(true);
      setMsg(null);
      try {
        await signupSubmit({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          password: pwd,
        });
        setMsg({ type: "ok", text: "Compte créé ! Vérifiez votre e-mail pour confirmer." });
        onSuccess && onSuccess({ firstName, lastName, email });
      } catch (e) {
        setMsg({ type: "err", text: e?.message || "Impossible de créer le compte." });
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setMsg(null);
    setTimeout(() => {
      setLoading(false);
      setMsg({ type: "ok", text: "Compte créé ! Vérifiez votre e-mail pour confirmer." });
      setTimeout(() => onSuccess && onSuccess({ firstName, lastName, email }), 2000);
    }, 1000);
  };

  // Force de mot de passe
  const strength = !pwd ? 0 : [
    pwd.length >= 8,
    pwd.length >= 12,
    /[A-Z]/.test(pwd),
    /[0-9]/.test(pwd),
    /[^A-Za-z0-9]/.test(pwd),
  ].filter(Boolean).length;

  const strengthMeta = [
    { label: "", color: C.red },
    { label: "Très faible", color: C.red },
    { label: "Faible", color: C.red },
    { label: "Moyen", color: C.amber },
    { label: "Fort", color: C.green },
    { label: "Très fort", color: C.green },
  ][strength] || { label: "", color: C.red };

  return (
    <div style={{ width:"100%", maxWidth:480,
      borderRadius:24, border:`1px solid ${th.line}`, background:th.paper,
      boxShadow:"0 24px 64px rgba(16,36,24,.13)", overflow:"hidden",
      animation:"fadeUp .35s ease both" }}>

      <AuthHeader
        title="Créer votre compte"
        sub="Rejoignez la plateforme d'analyse Invelps">
        {/* Bouton retour */}
        <button onClick={onGoLogin}
          style={{ background:"none", border:"none", color:"rgba(255,255,255,.5)",
            fontSize:12, fontWeight:700, cursor:"pointer", padding:0,
            display:"flex", alignItems:"center", gap:5, marginBottom:18, position:"relative" }}>
          ← Retour à la connexion
        </button>

        {/* Stepper */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:18, position:"relative" }}>
          {[
            {n:1, label:"Informations"},
            {n:2, label:"Sécurité"},
          ].map(({n, label}, idx) => (
            <div key={n} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:24, height:24, borderRadius:999,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:10, fontWeight:800, transition:"all .2s",
                background: step > n ? C.green : step === n ? C.gold : "rgba(255,255,255,.15)",
                color: step >= n ? "#fff" : "rgba(255,255,255,.4)",
                border: step === n ? `2px solid ${C.gold}` : "2px solid transparent" }}>
                {step > n ? "✓" : n}
              </div>
              <span style={{ fontWeight:700, transition:"color .2s",
                fontSize:"clamp(9px,2.5vw,11px)",
                color: step >= n ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.35)" }}>
                {label}
              </span>
              {idx === 0 && (
                <div style={{ width:28, height:1, marginLeft:2,
                  background: step > n ? `${C.green}88` : "rgba(255,255,255,.18)" }}/>
              )}
            </div>
          ))}
        </div>
      </AuthHeader>

      <div style={{ padding:"clamp(20px,5vw,32px) clamp(16px,5vw,40px) 28px" }}>
        <Feedback msg={msg}/>

        {/* ── ÉTAPE 1 ── */}
        {step === 1 && (
          <div style={{ animation:"fadeUp .25s ease both" }}>

            {/* Prénom + Nom */}
            <div className="inv-grid-2" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:4 }}>
              {[
                {label:"Prénom *", field:"firstName", val:firstName, set:setFirstName, ph:"Marie"},
                {label:"Nom *",    field:"lastName",  val:lastName,  set:setLastName,  ph:"Dupont"},
              ].map(({label,field,val,set,ph}) => (
                <div key={field} style={{ marginBottom:10 }}>
                  <SLabel th={th}>{label}</SLabel>
                  <SInput th={th} value={val}
                    onChange={e=>set(e.target.value)}
                    onBlur={()=>touch(field)}
                    placeholder={ph}
                    error={touched[field] && errors[field]}/>
                  <FieldError text={touched[field] && errors[field]}/>
                </div>
              ))}
            </div>

            {/* Email */}
            <div style={{ marginBottom:14 }}>
              <SLabel th={th}>Adresse e-mail *</SLabel>
              <SInputIcon th={th} icon="✉" error={touched.email && errors.email}>
                <input value={email} onChange={e=>setEmail(e.target.value)}
                  onBlur={()=>touch("email")}
                  onKeyDown={e=>e.key==="Enter"&&nextStep()}
                  placeholder="marie.dupont@exemple.com" type="email"
                  style={{ ...iSt, color:th.ink }}/>
              </SInputIcon>
              <FieldError text={touched.email && errors.email}/>
            </div>

            <CtaButton onClick={nextStep}>Continuer →</CtaButton>
          </div>
        )}

        {/* ── ÉTAPE 2 ── */}
        {step === 2 && (
          <div style={{ animation:"fadeUp .25s ease both" }}>

            {/* Récap identité */}
            <div style={{ borderRadius:12, padding:"10px 14px", marginBottom:20,
              background:`${C.gold}0e`, border:`1px solid ${C.gold}33`,
              display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:34, height:34, borderRadius:999, background:`${C.gold}22`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:14, color:C.gold, fontWeight:800,
                fontFamily:"'Cormorant Garamond',serif", flexShrink:0 }}>
                {firstName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:800, color:th.ink }}>
                  {firstName} {lastName}
                </div>
                <div style={{ fontSize:10, color:th.muted, fontWeight:600,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {email}
                </div>
              </div>
              <button onClick={()=>setStep(1)}
                style={{ background:"none", border:"none", color:th.muted,
                  fontSize:10, cursor:"pointer", fontWeight:700, flexShrink:0 }}>
                Modifier
              </button>
            </div>

            {/* Mot de passe */}
            <div style={{ marginBottom:8 }}>
              <SLabel th={th}>Mot de passe *</SLabel>
              <SInputIcon th={th} icon="◈" error={touched.pwd && errors.pwd}
                extra={
                  <button onClick={()=>setShowPwd(s=>!s)}
                    style={{ background:"none", border:"none", color:th.muted,
                      fontSize:10, cursor:"pointer", flexShrink:0, padding:0 }}>
                    {showPwd?"Cacher":"Voir"}
                  </button>
                }>
                <input value={pwd}
                  onChange={e=>{ setPwd(e.target.value); touch("pwd"); }}
                  onBlur={()=>touch("pwd")}
                  placeholder="Minimum 8 caractères"
                  type={showPwd?"text":"password"}
                  style={{ ...iSt, color:th.ink }}/>
              </SInputIcon>
              <FieldError text={touched.pwd && errors.pwd}/>
            </div>

            {/* Barre de force */}
            {pwd.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ display:"flex", gap:4, marginBottom:5 }}>
                  {[1,2,3,4,5].map(i => (
                    <div key={i} style={{ flex:1, height:3, borderRadius:999, transition:"background .2s",
                      background: i <= strength ? strengthMeta.color : th.line }}/>
                  ))}
                </div>
                <div style={{ fontSize:10, fontWeight:700, color:strengthMeta.color }}>
                  {strengthMeta.label}
                </div>
              </div>
            )}

            {/* Confirmation */}
            <div style={{ marginBottom:20 }}>
              <SLabel th={th}>Confirmer le mot de passe *</SLabel>
              <SInputIcon th={th} icon="◈"
                error={touched.pwd2 && errors.pwd2}
                borderColor={
                  pwd2 && pwd2 === pwd ? C.green
                  : pwd2 && pwd2 !== pwd ? C.red
                  : undefined
                }
                extra={
                  pwd2 && pwd2 === pwd
                    ? <span style={{ fontSize:14, color:C.green, flexShrink:0 }}>✓</span>
                    : null
                }>
                <input value={pwd2}
                  onChange={e=>{ setPwd2(e.target.value); touch("pwd2"); }}
                  onKeyDown={e=>e.key==="Enter"&&submit()}
                  placeholder="Répétez votre mot de passe"
                  type={showPwd?"text":"password"}
                  style={{ ...iSt, color:th.ink }}/>
              </SInputIcon>
              <FieldError text={touched.pwd2 && errors.pwd2}/>
            </div>

            <CGUCheck th={th}/>

            <CtaButton onClick={submit} loading={loading}>
              Créer mon compte
            </CtaButton>

            <div style={{ textAlign:"center", marginTop:12 }}>
              <button onClick={()=>setStep(1)}
                style={{ background:"none", border:"none", color:th.muted,
                  fontSize:11, fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>
                ← Retour
              </button>
            </div>
          </div>
        )}

        <OAuthRow th={th} googleUrl={googleUrl} linkedinUrl={linkedinUrl}/>
      </div>

      <div style={{ padding:"14px clamp(16px,5vw,40px) 20px", borderTop:`1px solid ${th.line2}`, textAlign:"center" }}>
        <div style={{ fontSize:11, color:th.muted, fontWeight:600 }}>
          Déjà un compte ?{" "}
          <button onClick={onGoLogin}
            style={{ background:"none", border:"none", color:C.gold,
              fontWeight:800, fontSize:11, cursor:"pointer", padding:0 }}>
            Se connecter
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── RACINE — InvelpsAuth ────────────────────────────────────── */
export default function InvelpsAuth({
  initialPage = "login",
  onSuccess,
  googleAuthUrl = "/auth/google",
  linkedinAuthUrl = "/auth/linkedin",
  loginSubmit,
  signupSubmit,
  onForgotPassword,
  onGoSignup: onGoSignupExternal,
  onGoLogin: onGoLoginExternal,
}) {
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDark] = useState(prefersDark);
  const [page, setPage] = useState(initialPage);
  const th = isDark ? DARK : LIGHT;

  useEffect(() => {
    syncTheme(isDark ? DARK : LIGHT);
  }, [isDark]);

  useEffect(() => {
    if (!document.getElementById("inv-auth-css")) {
      const s = document.createElement("style");
      s.id = "inv-auth-css";
      s.textContent = GLOBAL_CSS;
      document.head.appendChild(s);
    }
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: isDark
        ? `radial-gradient(700px 300px at 50% -5%,rgba(181,154,91,.07),transparent 55%), ${th.appBg}`
        : `radial-gradient(700px 280px at 50% -5%,rgba(181,154,91,.14),transparent 55%), ${th.appBg}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflowX: "hidden",
      padding: "16px 12px",
      transition: "background .3s",
    }}
    >
      {page === "login" ? (
        <LoginPage
          th={th}
          onGoSignup={() =>
            onGoSignupExternal ? onGoSignupExternal() : setPage("signup")
          }
          onSuccess={onSuccess}
          googleUrl={googleAuthUrl}
          linkedinUrl={linkedinAuthUrl}
          loginSubmit={loginSubmit}
          onForgotPassword={onForgotPassword}
        />
      ) : (
        <SignupPage
          th={th}
          onGoLogin={() =>
            onGoLoginExternal ? onGoLoginExternal() : setPage("login")
          }
          onSuccess={onSuccess}
          googleUrl={googleAuthUrl}
          linkedinUrl={linkedinAuthUrl}
          signupSubmit={signupSubmit}
        />
      )}
    </div>
  );
}
